import composeEventHandlers from '@/helpers/composeEventHandlers'
import { useComposeRefs } from '@/hooks/useComposeRefs'
import useGetCssValue from '@/hooks/useGetCssValue'
import useMediaQuery from '@/hooks/useMediaQuery'
import React, {
  AllHTMLAttributes,
  ForwardedRef,
  forwardRef,
  KeyboardEventHandler,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { clamp } from '../tabs/helpers'
import Backdrop from './Backdrop'
import { createPortal } from 'react-dom'

interface IModal extends AllHTMLAttributes<HTMLDivElement> {
  onClose: () => void
  className?: string
  open: boolean
  variant?: 'xl' | 'lg' | 'sm' | 'md'
  children: React.ReactNode
  disableBackdrop?: boolean
  blur?: boolean
  reEvaluate?: boolean
  key?: any
}

function NewModal(
  {
    onClose,
    open,
    className,
    children,
    variant = 'sm',
    disableBackdrop,
    blur = true,
    onKeyDown,
    reEvaluate = false,
    style,
    ...rest
  }: IModal,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const sizesMap = {
    sm: { sm: 343, md: 463, lg: 463 },
    md: { sm: 343, md: 660, lg: 648 },
    lg: { sm: 443, md: 770, lg: 824 },
    xl: { sm: 443, md: 824, lg: 1184 },
  }

  const [rand, setRand] = useState('')
  const lastFocusedElement = useRef<HTMLElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!reEvaluate || !open) return
    const timer = setInterval(() => {
      setRand(Math.random().toString(16).substring(3, 14))
    }, 2000)
    return () => clearInterval(timer)
  }, [reEvaluate, open])

  const isMobile = useMediaQuery('(max-width: 640px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  const isLaptop = useMediaQuery('(min-width: 780px)')
  const bodyHeight = window.innerHeight + 'px'
  const bodyWidth = window.innerWidth + 'px'

  const defaultModalWidth =
    sizesMap[variant][isMobile ? 'sm' : isTablet ? 'md' : 'lg']
  const defaultModalWidthClass = `w-[${defaultModalWidth}px]`

  const [computedRef, dialogHeight] = useGetCssValue(
    'height',
    '535px',
    rand || open
  )
  const combinedRef = useComposeRefs(computedRef, dialogRef, forwardedRef)

  useEffect(() => {
    if (open) {
      lastFocusedElement.current = document.activeElement as HTMLElement
      dialogRef.current?.focus()
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      lastFocusedElement.current?.focus()
    }
  }, [open])

  const generatedId = useId()
  const portalContainer = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const container = createModalContainer(generatedId)
    portalContainer.current = container

    return () => {
      if (container.parentNode === document.body) {
        document.body.removeChild(container)
      }
    }
  }, [generatedId])

  const keydownHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Escape') {
      onClose?.()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      const focusableElements =
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, a, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]
        if (e.shiftKey && document.activeElement === firstElement) {
          lastElement.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          firstElement.focus()
        }
      }
    }
  }
  function createModalContainer(uniqueId: string): HTMLElement {
    const el = document.createElement('div')
    el.id = `modal-root-${uniqueId}`
    document.body.appendChild(el)
    return el
  }

  if (!open || !portalContainer.current) return null

  const modal = (
    <Backdrop
      blur={blur}
      open={open}
      onClose={disableBackdrop ? () => {} : onClose}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='modal-title'
        onKeyDown={composeEventHandlers(onKeyDown, keydownHandler)}
        ref={combinedRef}
        tabIndex={open ? 0 : -1}
        onClick={(e) => e.stopPropagation()}
        style={{
          transitionTimingFunction: 'ease-in-out',
          transform: open ? 'scale(1,1)' : 'scale(0,0)',
          opacity: open ? '1' : '0',
          transitionProperty: 'transform, opacity',
          maxWidth: `calc(100vw - ${
            (isMobile ? 16 : isTablet ? 32 : 100) * 2
          }px)`,
          maxHeight: `calc(100vh - ${(isMobile || isLaptop ? 30 : 36) * 2}px)`,
          left: `${clamp(
            parseInt(bodyWidth) / 2 - defaultModalWidth / 2,
            isMobile ? 16 : isTablet ? 32 : 100,
            parseInt(bodyWidth) / 2
          )}px`,
          width: defaultModalWidth,
          top: `${clamp(
            parseInt(bodyHeight) / 2 - parseInt(dialogHeight) / 2,
            isMobile || isLaptop ? 30 : 36,
            parseInt(bodyHeight) / 2 - (isMobile || isLaptop ? 30 : 36)
          )}px`,
          ...style,
        }}
        {...rest}
        className={`bg-jaa-shades-white scrollbar scrollbar-w-1 scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg scrollbar-thumb-jaa-dark-teal-80 scrollbar-track-gray-100 ${defaultModalWidthClass} fixed rounded-lg overflow-auto shadow-md duration-300
        flex flex-col gap-6 justify-start items-start
        ${className || ''}`}
      >
        <h2 id='modal-title' className='sr-only'>
          Modal Content
        </h2>
        {children}
      </div>
    </Backdrop>
  )

  return createPortal(modal, portalContainer.current)
}

const Modal = forwardRef<HTMLDivElement, IModal>((props, ref) =>
  NewModal(props, ref)
)
Modal.displayName = 'Modal'
export default Modal
